// Én klubb i klubbportalen (/klubber/:id): klubbavtalen med rabattene, fast informasjon,
// innlegg (beskjeder fra klubben, f.eks. tøy som skal hentes) og trenere, innkjøpsansvarlige og
// andre kontakter per særidrett. Alle ansatte leser; leder og klubbansvarlig skriver. Ferdige
// innlegg arkiveres og ligger i «Arkiv» nederst, der de kan hentes tilbake.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Tilbake, Sidetopp, Valgknapper, feltKlasse, knappSekundar } from '../../ny-kundesamhandling/Skjema';
import { FARGER } from '../../ny-kundesamhandling/handlinger';
import Ikon from '../../ny-kundesamhandling/Ikon';
import AvtaleLogo from '../../components/AvtaleLogo';
import { getAgreementStatus, formatDate } from '../../constants/avtaler';
import { useKanSkriveKlubb } from '../../employeeAccess';
import { butikkNavn, klubbApi, rolleNavn, sarIdrettNavn, formatDato, ROLLER } from './klubbApi';
import { KlubbSkjema, PersonSkjema, InnleggSkjema, Kalkyle } from './Skjemaer';
import { linjeTall, kr, prosent } from '../../tilbud/kalkyle';

const merke = 'inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-semibold';
const kort = 'rounded-2xl border-2 border-slate-200 bg-white p-3';
const liteKnapp =
  'inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-xl border-2 border-slate-200 bg-white px-2.5 py-1 text-sm font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50';

const STATUS_FARGE = {
  AKTIV: 'bg-emerald-100 text-emerald-900',
  UTLOPER_SNART: 'bg-amber-100 text-amber-950',
  UTLOPT: 'bg-red-100 text-red-900',
  LOPENDE: 'bg-slate-200 text-slate-800',
};

function Avtale({ avtale }) {
  if (!avtale) {
    return (
      <section aria-labelledby="ks-avtale" className={kort}>
        <h2 id="ks-avtale" className="text-lg font-bold text-slate-900">Klubbavtale</h2>
        <p className="mt-1 text-slate-700">
          Ingen avtale koblet. Avtaler med rabatter legges inn under <Link to="/avtaler" className="font-semibold text-indigo-700 underline">Avtaler</Link> og kobles til klubben med «Endre».
        </p>
      </section>
    );
  }
  const status = getAgreementStatus(avtale.expiryDate);
  return (
    <section aria-labelledby="ks-avtale" className={`${kort} space-y-2`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="ks-avtale" className="text-lg font-bold text-slate-900">Klubbavtale</h2>
        <span className={`${merke} ${STATUS_FARGE[status.key]}`}>{status.label}</span>
      </div>
      <p className="whitespace-pre-wrap text-slate-800 [overflow-wrap:anywhere]">{avtale.discountDetails}</p>
      {avtale.discountCode && (
        <p className="flex flex-wrap items-center gap-2 text-slate-700">
          Rabattkode
          <span className="rounded-lg bg-slate-100 px-2 py-0.5 font-mono font-semibold text-slate-900 [overflow-wrap:anywhere]">{avtale.discountCode}</span>
        </p>
      )}
      {avtale.contactInfo && <p className="whitespace-pre-wrap text-slate-700 [overflow-wrap:anywhere]"><span className="font-semibold">Kontakt:</span> {avtale.contactInfo}</p>}
      {avtale.notes && <p className="whitespace-pre-wrap text-slate-700 [overflow-wrap:anywhere]"><span className="font-semibold">Notater:</span> {avtale.notes}</p>}
      <p className="text-sm text-slate-600">
        {avtale.startDate ? `Fra ${formatDate(avtale.startDate)}. ` : ''}
        {avtale.expiryDate ? `Utløper ${formatDate(avtale.expiryDate)}.` : 'Uten sluttdato.'}
      </p>
      <Link to={`/avtaler/${avtale._id}`} className="inline-flex items-center gap-1 font-semibold text-indigo-700 underline-offset-2 hover:underline">
        Åpne avtalen <Ikon navn="pil" className="w-4 h-4" />
      </Link>
    </section>
  );
}

// Hvem som har merket beskjeden som lest på kontrollpanelet (Ansattmelding). Bare for leder og
// klubbansvarlig.
function Lesestatus({ startside }) {
  const { vises, lest = [], ikkeLest = [] } = startside;
  const totalt = lest.length + ikkeLest.length;
  return (
    <details className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
      <summary className="cursor-pointer font-semibold">
        {vises ? 'Vises på kontrollpanelet' : 'Tatt av kontrollpanelet'} · lest av {lest.length} av {totalt}
      </summary>
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <div className="min-w-0">
          <h4 className="font-semibold">Har lest ({lest.length})</h4>
          {lest.length ? (
            <ul className="mt-1 space-y-0.5">
              {lest.map((l) => <li key={l.navn} className="[overflow-wrap:anywhere]">{l.navn} <span className="text-slate-600">· {formatDato(l.dato)}</span></li>)}
            </ul>
          ) : <p className="mt-1 text-slate-600">Ingen ennå.</p>}
        </div>
        <div className="min-w-0">
          <h4 className="font-semibold">Har ikke lest ({ikkeLest.length})</h4>
          {ikkeLest.length ? (
            <ul className="mt-1 space-y-0.5">
              {ikkeLest.map((n) => <li key={n} className="[overflow-wrap:anywhere]">{n}</li>)}
            </ul>
          ) : <p className="mt-1 text-slate-600">Alle har lest.</p>}
        </div>
      </div>
    </details>
  );
}

// Varene på en bestilling: merke og produkt, antall, pris, rabatt i kr og %, og sum per linje.
function Varer({ linjer }) {
  return (
    <div tabIndex={0} role="region" aria-label="Varer i bestillingen, kan rulles sidelengs"
      className="overflow-x-auto rounded-xl border border-slate-200 bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-slate-100 font-semibold text-slate-700">
          <tr>
            <th scope="col" className="px-2 py-1.5">Merke og produkt</th>
            <th scope="col" className="px-2 py-1.5 text-right">Antall</th>
            <th scope="col" className="px-2 py-1.5 text-right">Pris</th>
            <th scope="col" className="px-2 py-1.5 text-right">Rabatt %</th>
            <th scope="col" className="px-2 py-1.5 text-right">Rabatt kr</th>
            <th scope="col" className="px-2 py-1.5 text-right">Sum</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 text-slate-800">
          {linjer.map((l, i) => {
            const t = linjeTall(l);
            return (
              <tr key={i} className="align-top">
                <td className="min-w-[12rem] px-2 py-1.5 [overflow-wrap:anywhere]">
                  {l.artikkel || 'Vare'}
                  {l.ean && <span className="block font-mono text-slate-600">{l.ean}</span>}
                </td>
                <td className="px-2 py-1.5 text-right">{Math.max(1, Math.round(Number(l.antall) || 1))}</td>
                <td className="whitespace-nowrap px-2 py-1.5 text-right">{kr(l.pris)}</td>
                <td className="whitespace-nowrap px-2 py-1.5 text-right">{prosent(l.rabatt)}</td>
                <td className="whitespace-nowrap px-2 py-1.5 text-right">{kr(t.rabattKr)}</td>
                <td className="whitespace-nowrap px-2 py-1.5 text-right font-semibold">{kr(t.total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// «Gjør om til ordre»: velg kontaktpersonen i klubben (trener, innkjøpsansvarlig eller annen kontakt), ikke en
// kunde i kunderegisteret. Det lages en Aktiv Klubb-bestilling med varene, som står i trykklisten
// og kan overføres til trykk. Personer i samme særidrett som bestillingen står først.
const KONTAKTROLLER = ['trener', 'innkjopsansvarlig', 'kontakt'];
function TilOrdre({ klubb, innlegg, onEndret }) {
  const navigate = useNavigate();
  const [apen, setApen] = useState(false);
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState('');
  const kandidater = klubb.personer
    .filter((p) => KONTAKTROLLER.includes(p.rolle))
    .sort((a, b) =>
      (Number((b.sarIdrettId || '') === (innlegg.sarIdrettId || '')) - Number((a.sarIdrettId || '') === (innlegg.sarIdrettId || ''))) ||
      a.navn.localeCompare(b.navn, 'nb'));
  async function lag(person) {
    setLagrer(true);
    setFeil('');
    try {
      const svar = await klubbApi(`/innlegg/${innlegg._id}/til-ordre`, { method: 'POST', body: { personId: person._id } });
      onEndret(svar.innlegg);
      navigate(`/order-details/${svar.ordre._id}`);
    } catch (err) {
      setFeil(err.message);
      setLagrer(false);
    }
  }
  if (!apen) {
    return (
      <button type="button" onClick={() => setApen(true)} className={liteKnapp} disabled={!innlegg.prislinjer?.length}>
        <Ikon navn="kvittering" className="w-4 h-4" /> Gjør om til ordre<span className="sr-only">: {innlegg.tittel}</span>
      </button>
    );
  }
  return (
    <div role="group" aria-labelledby={`tilordre-${innlegg._id}`} className="w-full space-y-2 rounded-xl border border-violet-300 bg-violet-50 p-2.5">
      <p id={`tilordre-${innlegg._id}`} className="font-semibold text-slate-900">Hvem i klubben skal ordren stå på?</p>
      <p className="text-sm text-slate-700">
        Ordren står på {klubb.navn} og kontaktpersonen du velger, ikke på en kunde. Den får status «Aktiv Klubb-bestilling» med varene
        og prisene, og står i trykklisten.
      </p>
      {lagrer ? <p role="status">Lager ordren …</p> : kandidater.length ? (
        <ul className="space-y-1.5">
          {kandidater.map((p) => {
            const sar = sarIdrettNavn(klubb, p.sarIdrettId) || 'Hele klubben';
            return (
              <li key={p._id}>
                <button type="button" onClick={() => lag(p)}
                  className="flex w-full min-w-0 flex-wrap items-baseline gap-x-2 rounded-xl border-2 border-slate-200 bg-white px-2.5 py-1.5 text-left hover:bg-violet-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
                  <span className="font-semibold text-slate-900 [overflow-wrap:anywhere]">{p.navn}</span>
                  <span className="text-sm text-slate-700">{rolleNavn(p.rolle)} · {sar}{p.tittel ? ` · ${p.tittel}` : ''}</span>
                  {p.telefon ? <span className="font-mono text-sm text-slate-700">{p.telefon}</span> : <span className="text-sm text-amber-900">Ingen mobil</span>}
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-slate-800">
          Klubben har ingen registrerte personer ennå. Legg inn kontaktpersonen under «Trenere og innkjøpsansvarlige» først.
        </p>
      )}
      {feil && <p role="alert" className="font-semibold text-rose-800">{feil}</p>}
      <button type="button" onClick={() => setApen(false)} disabled={lagrer} className={liteKnapp}>Avbryt</button>
    </div>
  );
}

function Innlegg({ klubb, innlegg, kanSkrive, onEndret, onSlettet }) {
  const [endrer, setEndrer] = useState(false);
  if (endrer) {
    return (
      <InnleggSkjema
        klubb={klubb}
        innlegg={innlegg}
        onLagret={(i) => { setEndrer(false); onEndret(i); }}
        onAvbryt={() => setEndrer(false)}
      />
    );
  }
  async function slett() {
    if (!window.confirm(`Slette innlegget «${innlegg.tittel}»?`)) return;
    try {
      await klubbApi(`/innlegg/${innlegg._id}`, { method: 'DELETE' });
      onSlettet(innlegg._id);
    } catch (err) {
      window.alert(err.message);
    }
  }
  async function arkiver(tilbake) {
    try {
      onEndret(await klubbApi(`/innlegg/${innlegg._id}/${tilbake ? 'hent-tilbake' : 'arkiver'}`, { method: 'POST' }));
    } catch (err) {
      window.alert(err.message);
    }
  }
  const arkivert = !!innlegg.arkivert?.dato;
  const sar = sarIdrettNavn(klubb, innlegg.sarIdrettId);
  return (
    <article id={`innlegg-${innlegg._id}`} aria-labelledby={`innlegg-${innlegg._id}-t`} className={`${kort} scroll-mt-24 space-y-1.5 ${innlegg.festet ? 'border-amber-300 bg-amber-50/40' : ''}`}>
      <div className="flex flex-wrap gap-1.5">
        {arkivert && <span className={`${merke} bg-slate-200 text-slate-800`}>Arkivert</span>}
        {innlegg.festet && <span className={`${merke} bg-amber-100 text-amber-950`}>Festet</span>}
        {innlegg.type === 'bestilling' && <span className={`${merke} bg-violet-100 text-violet-900`}>Bestilling</span>}
        {innlegg.bareLedere && <span className={`${merke} bg-rose-100 text-rose-900`}>Bare leder og klubbansvarlig</span>}
        <span className={`${merke} bg-sky-100 text-sky-900`}>{sar || 'Hele klubben'}</span>
      </div>
      <h3 id={`innlegg-${innlegg._id}-t`} className="text-base font-bold text-slate-900 [overflow-wrap:anywhere]">{innlegg.tittel}</h3>
      {innlegg.tekst && <p className="whitespace-pre-wrap text-slate-800 [overflow-wrap:anywhere]">{innlegg.tekst}</p>}
      {innlegg.type === 'bestilling' && (innlegg.prislinjer?.length ? (
        <div className="space-y-2">
          <Varer linjer={innlegg.prislinjer} />
          <Kalkyle linjer={innlegg.prislinjer} />
        </div>
      ) : <p className="text-sm text-slate-600">Ingen varer lagt inn ennå.</p>)}
      {innlegg.ordre?.id && (
        <p className="text-sm font-semibold text-violet-900">
          Gjort om til{' '}
          <Link to={`/order-details/${innlegg.ordre.id}`} className="text-indigo-700 underline underline-offset-2">ordre {innlegg.ordre.ordreid}</Link>
          {' '}{formatDato(innlegg.ordre.dato)}{innlegg.ordre.av ? ` av ${innlegg.ordre.av}` : ''}.
          {kanSkrive && !arkivert && (
            <>
              {' '}
              <Link to={`/order-details/${innlegg.ordre.id}#ot-tilbud`} className="text-indigo-700 underline underline-offset-2">
                Send pristilbud til kontaktpersonen
              </Link>
            </>
          )}
        </p>
      )}
      <p className="text-sm text-slate-600">
        {formatDato(innlegg.createdAt)}{innlegg.opprettetAv ? `, ${innlegg.opprettetAv}` : ''}
        {innlegg.updatedAt && innlegg.updatedAt !== innlegg.createdAt && innlegg.endretAv
          ? ` · endret ${formatDato(innlegg.updatedAt)} av ${innlegg.endretAv}`
          : ''}
        {arkivert && ` · arkivert ${formatDato(innlegg.arkivert.dato)}${innlegg.arkivert.av ? ` av ${innlegg.arkivert.av}` : ''}`}
      </p>
      {kanSkrive && !arkivert && innlegg.startside && <Lesestatus startside={innlegg.startside} />}
      {kanSkrive && arkivert && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => arkiver(true)} className={liteKnapp}>
            Hent tilbake<span className="sr-only"> {innlegg.tittel}</span>
          </button>
          <button type="button" onClick={slett} className={liteKnapp}>
            Slett<span className="sr-only"> {innlegg.tittel}</span>
          </button>
        </div>
      )}
      {kanSkrive && !arkivert && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setEndrer(true)} className={liteKnapp}>
            <Ikon navn="blyant" className="w-4 h-4" /> Endre<span className="sr-only"> {innlegg.tittel}</span>
          </button>
          <button type="button" onClick={slett} className={liteKnapp}>
            Slett<span className="sr-only"> {innlegg.tittel}</span>
          </button>
          <button type="button" onClick={() => arkiver(false)} className={liteKnapp}>
            Arkiver<span className="sr-only"> {innlegg.tittel}</span>
          </button>
          {innlegg.type === 'bestilling' && !innlegg.ordre?.id && <TilOrdre klubb={klubb} innlegg={innlegg} onEndret={onEndret} />}
        </div>
      )}
    </article>
  );
}

function Person({ klubb, person, kanSkrive, onEndret, onSlettet }) {
  const [endrer, setEndrer] = useState(false);
  if (endrer) {
    return (
      <li>
        <PersonSkjema klubb={klubb} person={person} onLagret={(p) => { setEndrer(false); onEndret(p); }} onAvbryt={() => setEndrer(false)} />
      </li>
    );
  }
  async function slett() {
    if (!window.confirm(`Fjerne ${person.navn} fra ${klubb.navn}? Opplysningene slettes.`)) return;
    try {
      await klubbApi(`/klubber/${klubb._id}/personer/${person._id}`, { method: 'DELETE' });
      onSlettet(person._id);
    } catch (err) {
      window.alert(err.message);
    }
  }
  return (
    <li className="space-y-1 rounded-xl border border-slate-200 bg-white px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-semibold text-slate-900 [overflow-wrap:anywhere]">{person.navn}</span>
        <span className={`${merke} bg-slate-100 text-slate-800`}>{rolleNavn(person.rolle)}</span>
        {person.kanHenteMotFaktura && <span className={`${merke} bg-emerald-100 text-emerald-900`}>Kan hente på faktura</span>}
      </div>
      {person.tittel && <p className="text-slate-700 [overflow-wrap:anywhere]">{person.tittel}</p>}
      {(person.telefon || person.epost) && (
        <p className="flex flex-wrap gap-x-3 gap-y-1">
          {person.telefon && <a href={`tel:${person.telefon.replace(/\s/g, '')}`} className="text-indigo-700 underline-offset-2 hover:underline">{person.telefon}</a>}
          {person.epost && <a href={`mailto:${person.epost}`} className="text-indigo-700 underline-offset-2 hover:underline [overflow-wrap:anywhere]">{person.epost}</a>}
        </p>
      )}
      {person.merknad && <p className="whitespace-pre-wrap text-sm text-slate-700 [overflow-wrap:anywhere]">{person.merknad}</p>}
      {kanSkrive && (
        <div className="flex flex-wrap gap-2 pt-1">
          <button type="button" onClick={() => setEndrer(true)} className={liteKnapp}>
            <Ikon navn="blyant" className="w-4 h-4" /> Endre<span className="sr-only"> {person.navn}</span>
          </button>
          <button type="button" onClick={slett} className={liteKnapp}>
            Fjern<span className="sr-only"> {person.navn}</span>
          </button>
        </div>
      )}
    </li>
  );
}

// Personene for én særidrett (eller hele klubben), sortert etter rolle og navn.
function PersonGruppe({ klubb, tittel, sarIdrettId, personer, kanSkrive, onEndret, onSlettet, onNy }) {
  const sortert = [...personer].sort(
    (a, b) =>
      ROLLER.findIndex((r) => r.verdi === a.rolle) - ROLLER.findIndex((r) => r.verdi === b.rolle) ||
      a.navn.localeCompare(b.navn, 'nb')
  );
  const hId = `ks-gruppe-${sarIdrettId || 'klubb'}`;
  return (
    <section aria-labelledby={hId} className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={hId} className="font-bold text-slate-900">{tittel}</h3>
        {kanSkrive && (
          <button type="button" onClick={() => onNy(sarIdrettId)} className={liteKnapp}>
            <Ikon navn="pluss" className="w-4 h-4" /> Legg til<span className="sr-only"> person i {tittel}</span>
          </button>
        )}
      </div>
      {sortert.length === 0 ? (
        <p className="text-slate-600">Ingen registrert.</p>
      ) : (
        <ul className="space-y-1.5">
          {sortert.map((p) => (
            <Person key={p._id} klubb={klubb} person={p} kanSkrive={kanSkrive} onEndret={onEndret} onSlettet={onSlettet} />
          ))}
        </ul>
      )}
    </section>
  );
}

export default function KlubbSide() {
  const { id } = useParams();
  const [klubb, setKlubb] = useState(null);
  const [laster, setLaster] = useState(true);
  const [feil, setFeil] = useState('');
  const [endrerKlubb, setEndrerKlubb] = useState(false);
  const [nyttInnlegg, setNyttInnlegg] = useState(false);
  const [nyPerson, setNyPerson] = useState(null); // særidrett-id ('' = hele klubben) når skjemaet er åpent
  const [filter, setFilter] = useState('alle');
  const [q, setQ] = useState('');
  const kanSkrive = useKanSkriveKlubb();

  const hent = useCallback(() => {
    klubbApi(`/klubber/${id}`)
      .then((k) => { setKlubb(k); setFeil(''); })
      .catch((err) => setFeil(err.message))
      .finally(() => setLaster(false));
  }, [id]);

  // På nytt når ansatt byttes: leder og klubbansvarlig ser også innlegg som bare er for ledere.
  useEffect(() => { hent(); }, [hent, kanSkrive]);

  // Lenker fra søket peker til #innlegg-<id>.
  useEffect(() => {
    if (!klubb || !window.location.hash) return;
    const el = document.getElementById(window.location.hash.slice(1));
    if (!el) return;
    // Et arkivert innlegg ligger i den lukkede «Arkiv»-boksen.
    const arkivboks = el.closest('details');
    if (arkivboks) arkivboks.open = true;
    el.scrollIntoView({ block: 'start' });
  }, [klubb]);

  // Arkiverte innlegg ligger i «Arkiv» nederst og telles ikke med i søket over.
  const aktive = useMemo(() => (klubb ? klubb.innlegg.filter((i) => !i.arkivert?.dato) : []), [klubb]);
  const arkiv = useMemo(
    () => (klubb ? klubb.innlegg.filter((i) => i.arkivert?.dato).sort((a, b) => new Date(b.arkivert.dato) - new Date(a.arkivert.dato)) : []),
    [klubb]
  );
  const innlegg = useMemo(() => {
    const tekst = q.trim().toLowerCase();
    return aktive.filter((i) => {
      if (filter === 'klubb' && i.sarIdrettId) return false;
      if (filter !== 'alle' && filter !== 'klubb' && i.sarIdrettId !== filter) return false;
      return !tekst || [i.tittel, i.tekst, ...(i.prislinjer || []).map((l) => l.artikkel)].some((v) => v && v.toLowerCase().includes(tekst));
    });
  }, [aktive, filter, q]);

  if (laster) return <div className="mx-auto max-w-7xl"><p className="px-3 py-5 text-slate-700">Henter klubben …</p></div>;
  if (!klubb) {
    return (
      <div className="mx-auto max-w-7xl space-y-4">
        <Tilbake til="/klubber">Tilbake til klubbene</Tilbake>
        <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil || 'Fant ikke klubben.'}</p>
      </div>
    );
  }

  const settInnlegg = (fn) => setKlubb((k) => ({ ...k, innlegg: fn(k.innlegg) }));
  const sorterInnlegg = (l) => [...l].sort((a, b) => (b.festet - a.festet) || (new Date(b.createdAt) - new Date(a.createdAt)));
  const settPersoner = (fn) => setKlubb((k) => ({ ...k, personer: fn(k.personer) }));

  const personGrupper = [
    { id: '', tittel: 'Hele klubben' },
    ...klubb.sarIdretter.map((s) => ({ id: s._id, tittel: s.navn })),
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-10">
      <Tilbake til="/klubber">Tilbake til klubbene</Tilbake>

      <Sidetopp
        ikon="folk"
        farge="kunde"
        tittel={`${klubb.navn}${klubb.kortnavn ? ` (${klubb.kortnavn})` : ''}`}
        undertittel={`${butikkNavn(klubb.butikk)} · ${klubb.sarIdretter.map((s) => s.navn).join(', ') || 'Ingen særidretter'}${klubb.aktiv === false ? ' · Ikke aktiv' : ''}`}
      >
        {kanSkrive && !endrerKlubb && (
          <button type="button" onClick={() => setEndrerKlubb(true)} className={knappSekundar}>
            <Ikon navn="blyant" className="w-5 h-5" /> Endre klubben
          </button>
        )}
      </Sidetopp>

      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}

      {endrerKlubb && (
        <KlubbSkjema
          klubb={klubb}
          onLagret={() => { setEndrerKlubb(false); hent(); }}
          onAvbryt={() => setEndrerKlubb(false)}
        />
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="flex min-w-0 gap-3">
          <AvtaleLogo agreement={{ name: klubb.navn, logo: klubb.avtale?.logo }} storrelse="stor" />
          <div className="min-w-0 flex-1"><Avtale avtale={klubb.avtale} /></div>
        </div>
        <section aria-labelledby="ks-info" className={`${kort} min-w-0 space-y-2`}>
          <h2 id="ks-info" className="text-lg font-bold text-slate-900">Om klubben</h2>
          {klubb.info ? (
            <p className="whitespace-pre-wrap text-slate-800 [overflow-wrap:anywhere]">{klubb.info}</p>
          ) : (
            <p className="text-slate-600">Ingen fast informasjon ennå.</p>
          )}
          <p className="text-slate-700">
            <span className="font-semibold">Klubbansvarlig hos oss:</span>{' '}
            {klubb.klubbansvarlige.length ? klubb.klubbansvarlige.join(', ') : 'Ikke satt'}
          </p>
        </section>
      </div>

      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
        <section aria-labelledby="ks-innlegg" className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id="ks-innlegg" className="text-lg font-bold text-slate-900">Beskjeder fra klubben</h2>
            {kanSkrive && !nyttInnlegg && (
              <button
                type="button"
                onClick={() => setNyttInnlegg(true)}
                className="inline-flex min-h-[2.5rem] items-center gap-2 rounded-xl border-2 border-sky-200 bg-sky-50 px-3 py-1.5 font-bold text-slate-900 hover:bg-sky-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
              >
                <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${FARGER.kunde.ikon}`}><Ikon navn="pluss" className="w-5 h-5" /></span>
                Nytt innlegg
              </button>
            )}
          </div>

          {nyttInnlegg && (
            <InnleggSkjema
              klubb={klubb}
              onLagret={(i) => { setNyttInnlegg(false); settInnlegg((l) => sorterInnlegg([i, ...l])); }}
              onAvbryt={() => setNyttInnlegg(false)}
            />
          )}

          {aktive.length > 0 && (
            <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div>
                <label htmlFor="ks-sok" className="mb-1 block font-semibold text-slate-800">Søk i beskjedene</label>
                <input id="ks-sok" type="search" autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)} className={feltKlasse} aria-describedby="ks-sok-status" />
              </div>
              {klubb.sarIdretter.length > 0 && (
                <Valgknapper
                  id="ks-filter"
                  etikett="Vis beskjeder for"
                  valg={[
                    { verdi: 'alle', tittel: 'Alle' },
                    { verdi: 'klubb', tittel: 'Hele klubben' },
                    ...klubb.sarIdretter.map((s) => ({ verdi: s._id, tittel: s.navn })),
                  ]}
                  verdi={filter}
                  onVelg={setFilter}
                />
              )}
            </div>
          )}

          <p id="ks-sok-status" role="status" className="text-slate-700 empty:hidden">
            {(q.trim() || filter !== 'alle') ? `${innlegg.length} av ${aktive.length} beskjeder vises` : ''}
          </p>

          {innlegg.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">
              {aktive.length ? 'Ingen beskjeder passer søket.' : arkiv.length ? 'Ingen aktive beskjeder. De ferdige ligger i arkivet under.' : 'Ingen beskjeder ennå.'}
            </p>
          ) : (
            innlegg.map((i) => (
              <Innlegg
                key={i._id}
                klubb={klubb}
                innlegg={i}
                kanSkrive={kanSkrive}
                onEndret={(ny) => settInnlegg((l) => sorterInnlegg(l.map((x) => (x._id === ny._id ? ny : x))))}
                onSlettet={(iid) => settInnlegg((l) => l.filter((x) => x._id !== iid))}
              />
            ))
          )}

          {arkiv.length > 0 && (
            <details className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <summary className="cursor-pointer font-bold text-slate-900">Arkiv ({arkiv.length})</summary>
              <p className="mt-1 text-sm text-slate-700">Ferdige meldinger og bestillinger. «Hent tilbake» legger dem i listen over igjen.</p>
              <div className="mt-3 space-y-3">
                {arkiv.map((i) => (
                  <Innlegg
                    key={i._id}
                    klubb={klubb}
                    innlegg={i}
                    kanSkrive={kanSkrive}
                    onEndret={(ny) => settInnlegg((l) => sorterInnlegg(l.map((x) => (x._id === ny._id ? ny : x))))}
                    onSlettet={(iid) => settInnlegg((l) => l.filter((x) => x._id !== iid))}
                  />
                ))}
              </div>
            </details>
          )}
        </section>

        <section aria-labelledby="ks-personer" className="min-w-0 space-y-4">
          <h2 id="ks-personer" className="text-lg font-bold text-slate-900">Trenere og innkjøpsansvarlige</h2>
          {nyPerson !== null && (
            <PersonSkjema
              klubb={klubb}
              standardSarIdrett={nyPerson}
              onLagret={(p) => { setNyPerson(null); settPersoner((l) => [...l, p]); }}
              onAvbryt={() => setNyPerson(null)}
            />
          )}
          {personGrupper.map((g) => (
            <PersonGruppe
              key={g.id || 'klubb'}
              klubb={klubb}
              tittel={g.tittel}
              sarIdrettId={g.id}
              personer={klubb.personer.filter((p) => (p.sarIdrettId || '') === g.id)}
              kanSkrive={kanSkrive}
              onNy={setNyPerson}
              onEndret={(p) => settPersoner((l) => l.map((x) => (x._id === p._id ? p : x)))}
              onSlettet={(pid) => settPersoner((l) => l.filter((x) => x._id !== pid))}
            />
          ))}
        </section>
      </div>
    </div>
  );
}
